import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type MaestroStatus, type MaestroState } from "../../lib/api";

/**
 * Power control for Maestro Studio — the dedicated GPU video box.
 *
 * OFF BY DEFAULT. The box costs ~$1/hr running, so it lives stopped and is
 * started on demand from here. Start it, wait ~1-2 min for it to boot, then the
 * links light up. It stops itself after 60 min idle, so a forgotten box doesn't
 * keep billing. This card is the one always-on place to control it (the Maestro
 * URL itself is dead while the box is off).
 */

function stateView(s: MaestroState, ready: boolean): { label: string; dot: string; text: string } {
  if (s === "running" && ready) return { label: "Running", dot: "bg-emerald-500", text: "text-emerald-700" };
  if (s === "running" && !ready) return { label: "Starting up…", dot: "bg-amber-500 animate-pulse", text: "text-amber-700" };
  if (s === "pending") return { label: "Starting…", dot: "bg-amber-500 animate-pulse", text: "text-amber-700" };
  if (s === "stopping" || s === "shutting-down") return { label: "Stopping…", dot: "bg-amber-500 animate-pulse", text: "text-amber-700" };
  if (s === "stopped") return { label: "Off", dot: "bg-slate-300", text: "text-slate-500" };
  return { label: s, dot: "bg-slate-300", text: "text-slate-500" };
}

export function MaestroStudioCard() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery<MaestroStatus>({
    queryKey: ["maestro", "status"],
    queryFn: () => api.maestroStatus(),
    // Poll while it's in motion (booting/stopping) so the UI tracks it live;
    // sit still once it's settled.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return false;
      if (d.state === "pending" || d.state === "stopping" || d.state === "shutting-down") return 5000;
      if (d.state === "running" && !d.ready) return 5000;
      return false;
    },
  });

  const start = useMutation({
    mutationFn: () => api.maestroStart(),
    onMutate: () => setErr(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maestro", "status"] }),
    onError: (e: Error) => setErr(e.message),
  });
  const stop = useMutation({
    mutationFn: () => api.maestroStop(),
    onMutate: () => setErr(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["maestro", "status"] }),
    onError: (e: Error) => setErr(e.message),
  });

  if (isLoading || !data) return <div className="text-sm text-slate-500">Loading Maestro Studio…</div>;

  const busy = start.isPending || stop.isPending;
  const view = stateView(data.state, data.ready ?? false);
  const canStart = data.state === "stopped";
  const canStop = data.state === "running" || data.state === "pending";
  const booting = data.state === "pending" || (data.state === "running" && !data.ready);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Maestro Studio</h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          A dedicated GPU box for hand-crafting video in Maestro. It is <span className="font-medium">off by
          default</span> and costs about <span className="font-medium">$1/hour</span> while running. Start it here,
          wait a minute or two for it to boot, then open the studio. It stops itself after 60 minutes idle.
        </p>
      </div>

      {err && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${view.dot}`} />
            <span className={`text-sm font-medium ${view.text}`}>{view.label}</span>
            <code className="text-[11px] text-slate-400">{data.instanceId}</code>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !canStart}
              onClick={() => start.mutate()}
              className="rounded border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-40"
              title={canStart ? "Boot the GPU box" : "Only available when the box is off"}
            >
              {start.isPending ? "Starting…" : "Start"}
            </button>
            <button
              type="button"
              disabled={busy || !canStop}
              onClick={() => stop.mutate()}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              title={canStop ? "Stop the GPU box" : "Only available when the box is running"}
            >
              {stop.isPending ? "Stopping…" : "Stop"}
            </button>
          </div>
        </div>

        {booting && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Booting the box and starting Maestro — this usually takes 1-2 minutes. The links below light up when it is
            ready. (If AWS is out of GPU capacity, Start reports it — just try again.)
          </div>
        )}

        {data.state === "running" && data.ready && (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <a href={data.url} target="_blank" rel="noreferrer" className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-600">
              Open Maestro ↗
            </a>
            {data.studioUrl && (
              <a href={data.studioUrl} target="_blank" rel="noreferrer" className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-600">
                Open Studio ↗
              </a>
            )}
            <span className="text-xs text-slate-500">You'll be asked for the Maestro password.</span>
          </div>
        )}

        <p className="mt-3 text-[11px] text-slate-500">
          Open <span className="font-medium">Maestro</span> to launch/manage the app; once a render is running, the
          <span className="font-medium"> Studio</span> link is the workspace itself. Stops automatically after 60 min
          with no render, no open session, and no activity.
        </p>
      </div>
    </section>
  );
}
